/**
 * Donut showing credit spend per category for the last N transactions.
 * Pairs with SpendChart to give a "what am I spending on" answer at a
 * glance.
 */

import React, { useMemo } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { ResponsivePie } from '@nivo/pie';
import { useTheme } from '@mui/material/styles';

import { describeActivity, isSpendCategory, CATEGORY_COLORS } from './billingHelpers';

const CategoryDonut = ({ transactions = [], showLegend = true, height = 220 }) => {
  const theme = useTheme();

  const data = useMemo(() => {
    const totals = {};
    (transactions || []).forEach((txn) => {
      if (!txn?.amount || txn.amount >= 0) return;  // debits only
      const { category } = describeActivity(txn.reason);
      // Skip categories that represent credits flowing INTO the
      // balance (Grants, Top-ups, refunds) - those are not spend.
      if (!isSpendCategory(category)) return;
      totals[category] = (totals[category] || 0) + (-txn.amount);
    });
    return Object.entries(totals)
      .filter(([, v]) => v > 0)
      .sort(([, a], [, b]) => b - a)
      .map(([cat, v]) => ({
        id: cat,
        label: cat,
        value: Number(v.toFixed(2)),
        color: CATEGORY_COLORS[cat] || theme.palette.primary.main,
      }));
  }, [transactions, theme]);

  const total = data.reduce((s, d) => s + d.value, 0);

  if (!data.length) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No spend yet
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ height, position: 'relative' }}>
        <ResponsivePie
          data={data}
          margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
          innerRadius={0.65}
          padAngle={1.5}
          cornerRadius={4}
          activeOuterRadiusOffset={4}
          colors={(d) => d.data.color}
          borderWidth={0}
          enableArcLabels={false}
          enableArcLinkLabels={false}
          tooltip={({ datum }) => (
            <Box sx={{
              bgcolor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1, px: 1.5, py: 1,
              boxShadow: theme.shadows[4],
            }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: datum.color }}>
                {datum.label}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                {datum.value.toLocaleString()} credits ({((datum.value / total) * 100).toFixed(1)}%)
              </Typography>
            </Box>
          )}
          theme={{
            tooltip: { container: { background: 'transparent', boxShadow: 'none' } },
          }}
        />
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none',
        }}>
          <Typography variant="h5" fontWeight={700}>
            {total >= 1000 ? `${(total / 1000).toFixed(1)}k` : total.toFixed(0)}
          </Typography>
          <Typography variant="caption" color="text.secondary">credits spent</Typography>
        </Box>
      </Box>

      {showLegend && (
        <Stack direction="row" flexWrap="wrap" gap={1.5} justifyContent="center" sx={{ mt: 1.5 }}>
          {data.map((d) => (
            <Stack key={d.id} direction="row" alignItems="center" spacing={0.5}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: d.color }} />
              <Typography variant="caption" color="text.secondary">
                {d.label}{' '}
                <Typography component="span" variant="caption" sx={{ fontWeight: 600 }}>
                  {((d.value / total) * 100).toFixed(0)}%
                </Typography>
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default CategoryDonut;
